<template>
  <div
    class="side-block"
    :class="`side-block--${orientation}`"
    :style="{ opacity: noAnchor ? 0.4 : 1 }"
    @click="$emit('activate')"
  >
    <div class="side-block__head">
      <span class="side-block__title">{{ title }}</span>
      <span class="grey--text text-caption">{{ connectors.length }}</span>
      <v-spacer />
      <div class="side-block__add" :class="{ 'side-block__add--active': active && !noAnchor }">
        <IconTooltip
          icon-text="mdi-plus-thick"
          :text-tooltip="noAnchor ? 'Труба не доходит до края с этой стороны — стыковать нечего' : 'Добавить узел'"
          :color-icon="noAnchor ? 'grey' : 'green'"
          :is-disabled="loading || noAnchor"
          @click-icon="$emit('add')"
        />
      </div>
    </div>

    <div class="side-block__list">
      <div v-if="loading" class="side-block__loader">
        <v-progress-circular indeterminate size="28" width="3" color="green" />
      </div>
      <div
        v-for="conn in sortedConnectors"
        :key="conn.id"
        class="connector"
        :class="{ 'connector--mirrored': conn.mirrored }"
      >
        <IconTooltip
          icon-text="mdi-swap-horizontal"
          text-tooltip="Зеркальная связь — задана со страницы другого узла"
          color-icon="#9e9e9e"
          size-icon="18"
          :class="{ 'connector__mirror-hint--hidden': !conn.mirrored }"
          class="connector__mirror-hint flex-grow-0"
        />
        <div class="connector__node">
          <template v-if="nodeOf(conn.id_node)">
            <v-img
              v-if="nodeOf(conn.id_node)._file && nodeOf(conn.id_node)._file.url"
              :src="nodeOf(conn.id_node)._file.url"
              contain
              width="70"
              height="46"
              class="connector__thumb flex-grow-0"
            />
            <div v-else class="connector__thumb connector__thumb--empty flex-grow-0">
              <v-icon size="28" color="grey lighten-1">mdi-cog-outline</v-icon>
            </div>
            <span class="connector__code">{{ nodeOf(conn.id_node).code }}</span>
            <span
              v-if="nodeOf(conn.id_node).name_ru"
              class="grey--text text-caption text-truncate"
            >
              {{ nodeOf(conn.id_node).name_ru }}
            </span>
          </template>
          <span v-else class="red--text text-caption">узел не выбран</span>
        </div>
        <div class="connector__actions">
          <IconTooltip
            v-if="conn.id_node"
            icon-text="mdi-arrow-right-bold-circle-outline"
            text-tooltip="Открыть этот узел как центральный"
            color-icon="#5D80B5"
            @click-icon="$emit('open-node', conn.id_node)"
          />
          <IconTooltip
            icon-text="mdi-close"
            text-tooltip="Удалить связь"
            color-icon="red"
            @click-icon="$emit('remove', conn.id)"
          />
        </div>
      </div>

      <div
        v-if="!connectors.length && !loading"
        class="grey--text text-caption pa-2"
      >
        Пусто. Нажмите + справа в заголовке, чтобы добавить узел.
      </div>
    </div>
  </div>
</template>

<script>
import IconTooltip from "@/components/common/IconTooltip.vue";

const SIDE_TITLES = {
  top: "Подключение сверху",
  left: "Подключение слева",
  bottom: "Подключение снизу",
  right: "Подключение справа",
};

export default {
  name: "SideBlock",
  components: { IconTooltip },
  props: {
    side: { type: String, required: true },
    connectors: { type: Array, default: () => [] },
    active: { type: Boolean, default: false },
    nodeOf: { type: Function, required: true },
    loading: { type: Boolean, default: false },
    noAnchor: { type: Boolean, default: false },
  },
  computed: {
    title() {
      return SIDE_TITLES[this.side] || this.side;
    },
    orientation() {
      return this.side === "left" || this.side === "right"
        ? "vertical"
        : "horizontal";
    },
    // отображаем строки по алфавиту (по коду узла); строки без узла — в конце
    sortedConnectors() {
      const codeOf = (c) => {
        const n = this.nodeOf(c.id_node);
        return n && n.code ? n.code.toLowerCase() : null;
      };
      return [...this.connectors].sort((a, b) => {
        const ca = codeOf(a);
        const cb = codeOf(b);
        if (ca === cb) return 0;
        if (ca === null) return 1;
        if (cb === null) return -1;
        return ca.localeCompare(cb, "ru");
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.side-block {
  display: flex;
  flex-direction: column;
  align-self: start;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 8px;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.side-block__add {
  display: flex;
  border-radius: 50%;
  transition: box-shadow 0.15s, background-color 0.15s;
}

.side-block__add--active {
  background: #e8f5e9;
  box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.4);
}

.side-block__head {
  display: flex;
  align-items: center;
  grid-column-gap: 6px;
  margin-bottom: 6px;
}

.side-block__title {
  font-weight: 500;
  font-size: 13px;
}

.side-block__list {
  /* всегда ровно на 4 позиции — пустой блок такого же размера, что и заполненный */
  --row-gap: 6px;
  --row-h: 60px;
  position: relative;
  height: calc(var(--row-h) * 4 + var(--row-gap) * 3);
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  grid-row-gap: var(--row-gap);
}

/* лоадер поверх списка на время запроса схемы */
.side-block__loader {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.65);
}

/* Верхний/нижний блок: та же ширина, что у боковых, по центру над/под схемой. */
.side-block--horizontal {
  width: 320px;
  margin: 0 auto;
}

.connector {
  flex: 0 0 auto;
  height: var(--row-h);
  box-sizing: border-box;
  display: flex;
  align-items: center;
  grid-column-gap: 6px;
  background: #fff;
  border: 1px solid #eee;
  border-radius: 6px;
  padding: 4px 6px;
}

.connector--mirrored {
  background: #fafafa;
}

/* значок зеркальной связи; для обычных строк место резервируется, но скрыт */
.connector__mirror-hint--hidden {
  visibility: hidden;
}

.connector__node {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;
  grid-column-gap: 8px;
}

/* стрелка и крестик — только по наведению на строку */
.connector__actions {
  display: flex;
  align-items: center;
  grid-column-gap: 4px;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.12s;
}

.connector:hover .connector__actions {
  opacity: 1;
  pointer-events: auto;
}

.connector__thumb {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background: #fff;
}

.connector__thumb--empty {
  width: 70px;
  height: 46px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.connector__code {
  font-weight: 600;
  font-size: 13px;
}
</style>
